import { Box, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { Checkbox } from 'src/components-dummy';
import GenericSelect from '../GenericSelect/GenericSelect';
import BarChart from '../BarChart/BarChart';
import { getGranularityLabel, getKPILabel, KPIMetricsEnum, KpiGranularity } from './types';
import { Option } from '../GenericSelect/types';
import { useDispatch } from 'react-redux';
import { AppThunkDispatch } from 'src/app-types';
import { TimeRangeEnum } from '../FilterSection/TimeRangeSelector/types';
import { DeviceFilterEnum } from '../FilterSection/DeviceSelector/types';
import { kpiActions } from '../../actions';
import { useAppSelector } from 'src/hooks';
import { buildChartData, generateBarOptions } from '../BarChart/generateChartData';
import Skeleton from '@mui/material/Skeleton';

interface IOvertimeAnalysisProps {
  timeRange: TimeRangeEnum;
  product: string;
  device: DeviceFilterEnum;
  shopId: number;
}

export const OvertimeAnalysis = ({
  timeRange,
  product,
  device,
  shopId,
}: IOvertimeAnalysisProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { groupedKpiValuesList } = useAppSelector(state => state.dataInsights) as any;
  const [comparePreviousPeriod, setComparePreviousPeriod] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('syte_revenue');
  const [granularity, setGranularity] = useState<KpiGranularity>(KpiGranularity.Day);

  const handleCheckboxChange = (value: boolean) => {
    setComparePreviousPeriod(value);
  };

  const handleSelectChange = (newValue: string) => {
    setSelectedMetric(newValue);
  };
  const handleGranularityChange = (newValue: KpiGranularity) => {
    setGranularity(newValue);
  };
  const kpiMetricOptions: Option<KPIMetricsEnum>[] = Object.values(KPIMetricsEnum)
    .sort()
    .map(val => ({
      value: val,
      label: getKPILabel(val),
    }));

  const kpiData = groupedKpiValuesList[selectedMetric]?.data || [];
  const loading = groupedKpiValuesList[selectedMetric]?.loading;
  const error = groupedKpiValuesList[selectedMetric]?.error;

  const isAverageMetric = [
    KPIMetricsEnum.CTR,
    KPIMetricsEnum.CVR,
    KPIMetricsEnum.AOV,
    KPIMetricsEnum.ARPU,
  ].includes(selectedMetric as KPIMetricsEnum);
  const getUtcMonday = (date: Date) => {
    const utcDate = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    );
    const dayOfWeek = utcDate.getUTCDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    utcDate.setUTCDate(utcDate.getUTCDate() + diff);
    return utcDate;
  };
  const formatUtcDate = (date: Date) => date.toISOString().slice(0, 10);
  const groupKpiDataByGranularity = useMemo(() => {
    return (data: any[], bucket: KpiGranularity, range: TimeRangeEnum) => {
      if (bucket === KpiGranularity.Day) return data;

      if (bucket === KpiGranularity.Month && range === TimeRangeEnum.Last30Days) {
        const grouped = new Map<string, { sum: number; count: number; minDate: Date }>();
        data.forEach(item => {
          const date = new Date(`${item.kpi_label}T00:00:00Z`);
          if (Number.isNaN(date.getTime())) return;
          const key = item.period_type as string;
          const entry = grouped.get(key) || { sum: 0, count: 0, minDate: date };
          const minDate = date < entry.minDate ? date : entry.minDate;
          grouped.set(key, {
            sum: entry.sum + Number(item.value || 0),
            count: entry.count + 1,
            minDate,
          });
        });
        return Array.from(grouped.entries())
          .map(([periodType, entry]) => ({
            period_type: periodType,
            kpi_label: formatUtcDate(entry.minDate),
            value: isAverageMetric ? entry.sum / entry.count : entry.sum,
          }))
          .sort((a, b) => a.kpi_label.localeCompare(b.kpi_label));
      }

      const grouped = new Map<string, { sum: number; count: number }>();
      const keyFor = (label: string) => {
        const date = new Date(`${label}T00:00:00Z`);
        if (Number.isNaN(date.getTime())) return label;
        if (bucket === KpiGranularity.Week) {
          return formatUtcDate(getUtcMonday(date));
        }
        return formatUtcDate(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
      };

      data.forEach(item => {
        const key = `${item.period_type}|${keyFor(item.kpi_label)}`;
        const value = Number(item.value);
        const entry = grouped.get(key) || { sum: 0, count: 0 };
        grouped.set(key, {
          sum: entry.sum + (Number.isFinite(value) ? value : 0),
          count: entry.count + 1,
        });
      });

      const result: any[] = [];
      grouped.forEach((entry, key) => {
        const [periodType, kpiLabel] = key.split('|');
        const value = isAverageMetric ? entry.sum / entry.count : entry.sum;
        result.push({ period_type: periodType, kpi_label: kpiLabel, value });
      });

      return result.sort((a, b) => a.kpi_label.localeCompare(b.kpi_label));
    };
  }, [isAverageMetric]);

  const granularityOptions: Option<KpiGranularity>[] = [
    KpiGranularity.Day,
    KpiGranularity.Week,
    KpiGranularity.Month,
  ].map(val => ({
    value: val,
    label: getGranularityLabel(val),
  }));

  const isLast3Months = timeRange === TimeRangeEnum.Last3Months;
  const isCompareDisabled = isLast3Months;
  const effectiveGranularity =
    timeRange === TimeRangeEnum.Last30Days
      ? granularity
      : isLast3Months
        ? KpiGranularity.Month
        : KpiGranularity.Day;
  const shouldComparePreviousPeriod = comparePreviousPeriod && !isCompareDisabled;

  const displayKpiData = useMemo(
    () => groupKpiDataByGranularity(kpiData, effectiveGranularity, timeRange),
    [groupKpiDataByGranularity, kpiData, effectiveGranularity, timeRange]
  );

  const chartData = useMemo(
    () =>
      buildChartData(
        displayKpiData,
        timeRange,
        shouldComparePreviousPeriod,
        selectedMetric,
        effectiveGranularity
      ),
    [displayKpiData, timeRange, shouldComparePreviousPeriod, selectedMetric, effectiveGranularity]
  );

  const chartOptions = useMemo(
    () => generateBarOptions(chartData, selectedMetric, effectiveGranularity, timeRange),
    [chartData, selectedMetric, effectiveGranularity, timeRange]
  );
  const minPixelsPerLabel =
    timeRange === TimeRangeEnum.Last30Days
      ? shouldComparePreviousPeriod
        ? 24
        : 40
      : timeRange === TimeRangeEnum.Last3Months
        ? 120
        : timeRange === TimeRangeEnum.Last1Month && !shouldComparePreviousPeriod
          ? 45
          : 60;

  useEffect(() => {
    if (timeRange !== TimeRangeEnum.Last30Days && granularity !== KpiGranularity.Day) {
      setGranularity(KpiGranularity.Day);
    }
  }, [timeRange, granularity]);

  useEffect(() => {
    if (isCompareDisabled && comparePreviousPeriod) {
      setComparePreviousPeriod(false);
    }
  }, [isCompareDisabled, comparePreviousPeriod]);

  useEffect(() => {
    dispatch(
      kpiActions.getGroupedKpi({
        kpiType: selectedMetric,
        shopId,
        timeRange,
        product,
        device,
        compareToPreviousPeriod: shouldComparePreviousPeriod,
      })
    );
  }, [dispatch, selectedMetric, timeRange, product, device, shopId, shouldComparePreviousPeriod]);

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          Overtime Analysis
        </Typography>

        <Box display='flex' alignItems='center' gap={2}>
          <Checkbox
            checked={comparePreviousPeriod}
            label='Compare to previous period (UTC)'
            disabled={isCompareDisabled}
            onChange={handleCheckboxChange}
          />

          {timeRange === TimeRangeEnum.Last30Days && (
            <GenericSelect<KpiGranularity>
              value={granularity}
              onChange={event => handleGranularityChange(event.target.value as KpiGranularity)}
              icon={null}
              placeholder='Granularity...'
              options={granularityOptions}
            />
          )}

          <GenericSelect<string>
            value={selectedMetric}
            onChange={event => handleSelectChange(event.target.value)}
            icon={null}
            placeholder='Select metric...'
            options={kpiMetricOptions}
          />
        </Box>
      </Box>

      {loading ? (
        <Skeleton variant='rectangular' width='100%' height={400} />
      ) : error ? (
        <Box color='error.main' textAlign='center' py={4}>
          <Typography color='error'>{error}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
          }}
        >
          <Box
            sx={{
              minWidth: {
                xs: 320,
                sm: Math.max(chartData.labels.length * minPixelsPerLabel, 400),
              },
              maxWidth: '100%',
            }}
          >
            <BarChart data={chartData} options={chartOptions} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
